// Import i18n system and date utils
import i18n from '../../utils/i18n.js';
import DateUtils from '../../utils/DateUtils.js';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css'; // Tippy.js CSS
import 'tippy.js/themes/light-border.css'; // Light border theme

// Event Countdown System
class BceEventTeaser {
    constructor() {
        this.instances = new WeakMap();
        this.updateInterval = 60000; // 60 seconds
        this.autoInit = false;
    }

    /**
     * Initialize all event teaser elements found in the DOM
     * Automatically called by BCE system on page load
     */
    init() {
        const eventContainers = document.querySelectorAll('.bce_event-teaser');
        
        eventContainers.forEach(container => {
            if (!this.instances.has(container)) {
                this.create(container);
            }
        });
    }

    create(element, options = {}) {
        // Check if element already has an instance
        if (this.instances.has(element)) {
            return this.instances.get(element);
        }

        const manager = new EventCountdownManager(element, options);
        manager.init();
        
        this.instances.set(element, manager);
        return manager;
    }

    destroy(element) {
        const manager = this.instances.get(element);
        if (manager) {
            manager.destroy();
            this.instances.delete(element);
        }
    }


    // Method to get instance for external access
    getInstance(element) {
        return this.instances.get(element);
    }
}

class EventCountdownManager {
    constructor(containerElement, options = {}) {
        this.container = containerElement;
        this.options = {
            updateInterval: 60000, // 60 seconds
            ...options
        };
        this.intervals = [];
        this.tippyInstances = [];
    }

    init() {
        this.clearExistingCountdowns();
        this.processEvents();
        this.initTooltips();
    }

    destroy() {
        // Clear existing intervals
        this.intervals.forEach(interval => clearInterval(interval));
        this.intervals = [];
        
        // Destroy Tippy instances
        this.tippyInstances.forEach(instance => instance.destroy());
        this.tippyInstances = [];
        
        // Remove existing countdown elements
        this.container.querySelectorAll('.event-countdown').forEach(el => el.remove());
    }

    clearExistingCountdowns() {
        // Clear existing intervals
        this.intervals.forEach(interval => clearInterval(interval));
        this.intervals = [];
        
        // Remove existing countdown elements within this container
        this.container.querySelectorAll('.event-countdown').forEach(el => el.remove());
    }

    processEvents() {
        const events = this.container.querySelectorAll('.article');
        
        console.log('Event-Teaser Debug - Found articles:', events.length);
        
        events.forEach((event, index) => {
            console.log(`Event-Teaser Debug - Processing event ${index + 1}:`, event);
            
            // Prüfe ob Bild vorhanden ist und setze entsprechende Klasse
            this.handleImageLayout(event);
            
            const eventData = this.extractEventData(event);
            console.log('Event-Teaser Debug - Event data:', eventData);
            
            if (!eventData.startDate) {
                console.log('Event-Teaser Debug - No start date found, skipping event');
                return;
            }
            
            // Prüfe ob das Event komplett vorbei ist (Enddatum ist vergangen)
            const now = new Date();
            const eventEndDate = eventData.endDate || eventData.startDate;
            
            if (eventEndDate && eventEndDate < now) {
                console.log('Event-Teaser Debug - Event is completely over, hiding:', {
                    eventEnd: eventEndDate,
                    now: now,
                    title: eventData.title
                });
                
                // Verstecke das Event komplett
                event.style.display = 'none';
                return;
            }
            
            console.log('Event-Teaser Debug - Event is active, showing countdown');
            
            // Restrukturiere den Event-Inhalt
            this.restructureEventContent(event);
            
            const countdownElement = this.createCountdownElement(event);
            this.startCountdown(countdownElement, eventData);
        });
    }
    
    handleImageLayout(event) {
        const eventCard = event.querySelector('.event');
        const imageElement = event.querySelector('.event-img-wrap img, .event-img-wrap picture, .b_tm__picture');
        
        if (!imageElement || !eventCard) {
            // Kein Bild gefunden - setze no-image Klasse
            if (eventCard) {
                eventCard.classList.add('no-image');
                console.log('Event-Teaser Debug - No image found, added no-image class');
            }
        } else {
            // Bild vorhanden - entferne no-image Klasse falls gesetzt
            if (eventCard) {
                eventCard.classList.remove('no-image');
                console.log('Event-Teaser Debug - Image found, removed no-image class');
            }
        }
    }

    restructureEventContent(event) {
        const eventTextWrap = event.querySelector('.event-text-wrap');
        if (!eventTextWrap) return;
        
        // Sammle nur die gewünschten Elemente BEVOR wir den Container leeren
        const headline = event.querySelector('.event-headline');
        const date = event.querySelector('.event-date');
        const location = event.querySelector('.event-location');
        
        if (headline && date && location) {
            // Speichere die vollständigen Texte für Tooltips
            const fullHeadlineText = headline.textContent.trim();
            const fullLocationText = location.textContent.trim();
            
            // Klone die Elemente, bevor wir den Container leeren
            const headlineClone = headline.cloneNode(true);
            const dateClone = date.cloneNode(true);
            const locationClone = location.cloneNode(true);
            
            // Leere den Container komplett
            eventTextWrap.innerHTML = '';
            
            // Füge data-tippy-content zu den geklonten Elementen hinzu
            headlineClone.setAttribute('data-tippy-content', fullHeadlineText);
            locationClone.setAttribute('data-tippy-content', fullLocationText);
            
            // Füge nur die gewünschten Elemente hinzu
            eventTextWrap.appendChild(headlineClone);
            eventTextWrap.appendChild(dateClone);
            eventTextWrap.appendChild(locationClone);
        }
    }

    initTooltips() {
        // Check both hover support AND window width
        const supportsHover = window.matchMedia('(hover: hover)').matches;
        const isWideScreen = window.innerWidth > 768; // Same breakpoint as CSS
        
        if (supportsHover && isWideScreen) {
            // Initialize Tippy.js only for desktop with sufficient width
            setTimeout(() => {
                // Find all elements with truncated text within this container
                const truncatedElements = [];
                
                this.container.querySelectorAll('[data-tippy-content]').forEach(element => {
                    // Check if text is truncated by comparing scrollWidth with clientWidth
                    if (element.scrollWidth > element.clientWidth) {
                        truncatedElements.push(element);
                    }
                });
                
                // Only initialize Tippy.js for truncated elements
                if (truncatedElements.length > 0) {
                    const instances = tippy(truncatedElements, {
                        theme: 'light-border',
                        animation: 'fade',
                        duration: [200, 150],
                        delay: [300, 0],
                        placement: window.innerWidth > 1200 ? 'right' : 'top', // Dynamic placement
                        flip: true,
                        flipBehavior: ['right', 'left', 'top', 'bottom'],
                        popperOptions: {
                            modifiers: [
                                {
                                    name: 'preventOverflow',
                                    options: {
                                        boundary: 'viewport',
                                        padding: 8,
                                    },
                                },
                                {
                                    name: 'flip',
                                    options: {
                                        boundary: 'viewport',
                                        padding: 8,
                                    },
                                },
                            ],
                        }
                    });
                    
                    // Store instances for cleanup
                    this.tippyInstances.push(...(Array.isArray(instances) ? instances : [instances]));
                }
            }, 100);
        }
        // Mobile OR narrow desktop: No tooltips at all
    }

    extractEventData(event) {
        const dateElement = event.querySelector('.event-date');
        const locationElement = event.querySelector('.event-location');
        const headlineElement = event.querySelector('.event-headline');
        
        let eventDates = { startDate: null, endDate: null };
        
        if (dateElement) {
            const dateText = dateElement.textContent.trim();
            eventDates = DateUtils.parseEventDateRange(dateText);
            
            console.log('Event-Teaser Debug - Date element found:', dateElement);
            console.log('Event-Teaser Debug - Date text:', dateText);
            console.log('Event-Teaser Debug - Parsed dates:', eventDates);
        }
        
        return {
            startDate: eventDates.startDate,
            endDate: eventDates.endDate,
            location: locationElement ? locationElement.textContent.trim() : 'the venue',
            title: headlineElement ? headlineElement.textContent.trim() : 'event'
        };
    }

    parseEventDate(dateText) {
        // Use centralized date parsing utility
        return DateUtils.parseEventDate(dateText);
    }

    createCountdownElement(event) {
        const countdownDiv = document.createElement('div');
        countdownDiv.className = 'event-countdown';
        
        const eventTextWrap = event.querySelector('.event-text-wrap');
        if (eventTextWrap) {
            eventTextWrap.insertBefore(countdownDiv, eventTextWrap.firstChild);
        }
        
        return countdownDiv;
    }

    startCountdown(element, eventData) {
        console.log('Event-Teaser Debug - Starting countdown for:', eventData);
        
        const updateCountdown = () => {
            const now = Date.now();
            const timeLeft = eventData.startDate.getTime() - now;
            const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
            
            console.log('Event-Teaser Debug - Countdown update:', {
                now: new Date(now),
                eventDate: eventData.startDate,
                timeLeft,
                days
            });
            
            let content = '';
            
            if (timeLeft > 0) {
                content = this.getStandardCountdown(days);
                console.log('Event-Teaser Debug - Event upcoming, content:', content);
            } else {
                content = this.getEventStartedContent(eventData);
                console.log('Event-Teaser Debug - Event started, content:', content);
                Object.assign(element.style, {
                    borderLeftColor: '#28a745',
                    backgroundColor: '#d4edda'
                });
            }
            
            element.innerHTML = content;
            console.log('Event-Teaser Debug - Updated element HTML:', element.innerHTML);
        };
        
        // Initial update and set interval
        updateCountdown();
        const intervalId = setInterval(updateCountdown, this.options.updateInterval);
        this.intervals.push(intervalId);
        
        console.log('Event-Teaser Debug - Countdown initialized, interval ID:', intervalId);
    }

    getStandardCountdown(days) {
        const daysText = i18n.t('event', 'daysToGo', days);
        const soonText = i18n.t('event', 'startingSoon');
        
        console.log('Event-Teaser Debug - i18n translations:', { daysText, soonText });
        
        if (days >= 1) {
            return `<strong>${daysText}</strong>`;
        } else {
            return `<strong>${soonText}</strong>`;
        }
    }

    getEventStartedContent(eventData) {
        const visitText = i18n.t('event', 'visitNow');
        console.log('Event-Teaser Debug - Visit text translation:', visitText);
        
        return `<strong>${visitText}</strong><br>${eventData.location}`;
    }
}

export default new BceEventTeaser();